import PropTypes from 'prop-types';
import { useMemo, useEffect } from 'react';
import useSWR from 'swr';
import {
    Box, Container, Grid, Skeleton, Stack, Typography,
    alpha, useTheme, Chip,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
import { postFetcher, endpoints } from 'src/utils/axios';
import { useActivityLogger } from 'src/hooks/useActivityLogger';

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Helpers ───────────────────────────────────────────────────────────────────
function daysUntil(ds) {
    const t = new Date(); t.setHours(0,0,0,0);
    const h = new Date(ds); h.setHours(0,0,0,0);
    return Math.round((h - t) / 86400000);
}
const fmtShort   = (d) => new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short' });
const fmtWeekday = (d) => new Date(d).toLocaleDateString('en-IN', { weekday:'long' });

// ── Ticket Components ─────────────────────────────────────────────────────────

function GoldenTicket({ holiday }) {
    const theme = useTheme();
    if (!holiday) return null;
    const days = daysUntil(holiday.date);
    let tag = `${days} DAYS AWAY`;
    if (days === 0) tag = 'TODAY!';
    else if (days === 1) tag = 'TOMORROW!';

    return (
        <Box sx={{
            position: 'relative',
            mb: 8,
            mt: 4,
            display: 'flex',
            filter: `drop-shadow(0 20px 40px ${alpha(theme.palette.warning.main, 0.4)})`,
            transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            transformOrigin: 'center left',
            '&:hover': {
                transform: 'perspective(1200px) rotateY(-5deg) rotateX(2deg) translateY(-5px)',
            }
        }}>
            {/* The Stub (Left Side) */}
            <Box sx={{
                width: { xs: 80, sm: 120 },
                background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`,
                borderRadius: '24px 0 0 24px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                p: 2,
                position: 'relative',
                borderRight: `2px dashed ${alpha('#fff', 0.4)}`,
            }}>
                <Iconify icon="solar:star-fall-bold-duotone" width={40} sx={{ color: '#fff', mb: 1 }} />
                <Typography sx={{ color: '#fff', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 2, writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: '0.75rem' }}>
                    Golden Ticket
                </Typography>
            </Box>

            {/* Top Cutout */}
            <Box sx={{ position: 'absolute', top: -12, left: { xs: 80, sm: 120 }, transform: 'translateX(-50%)', width: 24, height: 24, borderRadius: '50%', bgcolor: 'background.default', zIndex: 2 }} />
            
            {/* Bottom Cutout */}
            <Box sx={{ position: 'absolute', bottom: -12, left: { xs: 80, sm: 120 }, transform: 'translateX(-50%)', width: 24, height: 24, borderRadius: '50%', bgcolor: 'background.default', zIndex: 2 }} />

            {/* The Details (Right Side) */}
            <Box sx={{
                flex: 1,
                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.light} 100%)`,
                borderRadius: '0 24px 24px 0',
                p: { xs: 3, sm: 4 },
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Visual Watermark */}
                <Box sx={{ position: 'absolute', right: -20, bottom: -40, opacity: 0.1, transform: 'rotate(-15deg)' }}>
                    <Iconify icon="solar:ticket-bold" width={200} />
                </Box>
                
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
                    <Typography sx={{ color: alpha('#fff', 0.8), fontSize: '0.8rem', letterSpacing: 3, textTransform: 'uppercase', fontWeight: 800 }}>Next Holiday</Typography>
                    <Chip label={tag} sx={{ fontWeight: 900, fontSize: '0.85rem', bgcolor: '#fff', color: theme.palette.warning.dark, borderRadius: '8px' }} />
                </Stack>
                
                <Typography variant="h2" sx={{ fontWeight: 900, color: '#fff', mb: 1, position: 'relative', zIndex: 1 }}>{holiday.name}</Typography>
                
                <Stack direction="row" alignItems="center" spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ px: 2, py: 1, bgcolor: alpha('#fff', 0.2), borderRadius: '8px', backdropFilter: 'blur(4px)' }}>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>{fmtWeekday(holiday.date)}</Typography>
                    </Box>
                    <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.2rem' }}>{fmtShort(holiday.date)}</Typography>
                </Stack>
            </Box>
        </Box>
    );
}
GoldenTicket.propTypes = { holiday: PropTypes.object };

function RegularTicket({ holiday, colorScheme, isPassed }) {
    const theme = useTheme();
    const days = daysUntil(holiday.date);

    return (
        <Box sx={{
            position: 'relative',
            mb: 3,
            display: 'flex',
            filter: isPassed 
                ? 'grayscale(100%) opacity(60%)' 
                : `drop-shadow(0 12px 24px ${alpha(theme.palette.grey[500], 0.15)})`,
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            transformOrigin: 'center left',
            cursor: isPassed ? 'default' : 'pointer',
            '&:hover': {
                transform: isPassed ? 'none' : 'perspective(1000px) rotateY(-3deg) rotateX(1deg) translateY(-3px)',
                filter: isPassed ? 'grayscale(100%) opacity(80%)' : `drop-shadow(0 20px 32px ${alpha(colorScheme.main, 0.25)})`,
            }
        }}>
            {/* The Stub (Left Side) */}
            <Box sx={{
                width: 90,
                bgcolor: isPassed ? alpha(theme.palette.grey[500], 0.2) : colorScheme.main,
                borderRadius: '16px 0 0 16px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                p: 2,
                position: 'relative',
                borderRight: `2px dashed ${alpha(isPassed ? theme.palette.text.disabled : colorScheme.main, 0.3)}`,
            }}>
                <Typography variant="h4" sx={{ color: isPassed ? 'text.secondary' : '#fff', fontWeight: 900, lineHeight: 1 }}>
                    {new Date(holiday.date).getDate()}
                </Typography>
                <Typography sx={{ color: isPassed ? 'text.secondary' : alpha('#fff', 0.8), fontWeight: 800, textTransform: 'uppercase', fontSize: '0.7rem', mt: 0.5, letterSpacing: 1 }}>
                    {fmtWeekday(holiday.date).substring(0,3)}
                </Typography>
            </Box>

            {/* Top Cutout */}
            <Box sx={{ position: 'absolute', top: -10, left: 90, transform: 'translateX(-50%)', width: 20, height: 20, borderRadius: '50%', bgcolor: 'background.default', zIndex: 2 }} />
            
            {/* Bottom Cutout */}
            <Box sx={{ position: 'absolute', bottom: -10, left: 90, transform: 'translateX(-50%)', width: 20, height: 20, borderRadius: '50%', bgcolor: 'background.default', zIndex: 2 }} />

            {/* The Details (Right Side) */}
            <Box sx={{
                flex: 1,
                bgcolor: 'background.paper',
                borderRadius: '0 16px 16px 0',
                p: 3,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>{holiday.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {isPassed ? 'Completed' : `Admit One · ${days} Days`}
                    </Typography>
                </Box>
                
                {/* Barcode Mockup */}
                {!isPassed && (
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: '2px', opacity: 0.2, height: 40 }}>
                        <Box sx={{ width: 2, bgcolor: 'text.primary' }} />
                        <Box sx={{ width: 4, bgcolor: 'text.primary' }} />
                        <Box sx={{ width: 1, bgcolor: 'text.primary' }} />
                        <Box sx={{ width: 3, bgcolor: 'text.primary' }} />
                        <Box sx={{ width: 5, bgcolor: 'text.primary' }} />
                        <Box sx={{ width: 2, bgcolor: 'text.primary' }} />
                        <Box sx={{ width: 1, bgcolor: 'text.primary' }} />
                        <Box sx={{ width: 4, bgcolor: 'text.primary' }} />
                    </Box>
                )}
                {isPassed && (
                    <Iconify icon="solar:check-circle-bold-duotone" width={32} sx={{ color: 'text.disabled' }} />
                )}
            </Box>
        </Box>
    );
}
RegularTicket.propTypes = { holiday: PropTypes.object, colorScheme: PropTypes.object, isPassed: PropTypes.bool };

// ── Header Stats Stamps ───────────────────────────────────────────────────────
function TicketWalletHeader({ holidays }) {
    const theme = useTheme();
    const today = new Date(); today.setHours(0,0,0,0);
    const upcoming = holidays.filter(h => { const d=new Date(h.date); d.setHours(0,0,0,0); return d>=today; }).length;

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: 'text.primary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Iconify icon="solar:ticket-bold-duotone" width={28} sx={{ color: 'background.paper' }} />
                </Box>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: -0.5 }}>Holiday Tickets</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>{new Date().getFullYear()} Season Pass</Typography>
                </Box>
            </Stack>

            {/* Stamps */}
            <Stack direction="row" spacing={3} sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Box sx={{ textAlign: 'center', px: 2, py: 1, border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`, borderRadius: '8px', transform: 'rotate(-2deg)' }}>
                    <Typography variant="h5" sx={{ color: theme.palette.primary.main, fontWeight: 900, lineHeight: 1 }}>{holidays.length}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>Total Issued</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 2, py: 1, border: `2px dashed ${alpha(theme.palette.success.main, 0.3)}`, borderRadius: '8px', transform: 'rotate(2deg)' }}>
                    <Typography variant="h5" sx={{ color: theme.palette.success.main, fontWeight: 900, lineHeight: 1 }}>{upcoming}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>Valid Remaining</Typography>
                </Box>
            </Stack>
        </Stack>
    );
}
TicketWalletHeader.propTypes = { holidays: PropTypes.array };

// ── Month Container ─────────────────────────────────────────────────────────────
function MonthTicketBatch({ monthIndex, holidayMap }) {
    const theme = useTheme();
    const holidays = Object.values(holidayMap);
    if (holidays.length === 0) return null;

    holidays.sort((a,b) => new Date(a.date) - new Date(b.date));
    
    // Rotating palette for ticket stubs
    const palettes = [theme.palette.primary, theme.palette.info, theme.palette.secondary, theme.palette.error];

    return (
        <Box sx={{ mb: 6 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3, pl: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 2 }}>
                    {MONTHS[monthIndex]} Batch
                </Typography>
                <Box sx={{ flexGrow: 1, height: 1, borderTop: `2px dotted ${theme.palette.divider}` }} />
            </Stack>

            <Grid container spacing={4}>
                {holidays.map((h, i) => {
                    const isPassed = daysUntil(h.date) < 0;
                    const palette = palettes[i % palettes.length];
                    return (
                        <Grid item xs={12} md={6} key={h._id || h.date}>
                            <RegularTicket holiday={h} colorScheme={palette} isPassed={isPassed} />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
}
MonthTicketBatch.propTypes = { monthIndex: PropTypes.number.isRequired, holidayMap: PropTypes.object.isRequired };

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skelly() {
    return (
        <Box>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '24px', mb: 8, mt: 4 }} />
            <Grid container spacing={4}>
                {[...Array(6)].map((_,i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Grid item xs={12} md={6} key={i}>
                        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '16px' }} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

// ── Main View ─────────────────────────────────────────────────────────────────
export default function HolidayCalendarCurrentYearListView() {
    const theme = useTheme();
    const yr = new Date().getFullYear();
    const { logVisit } = useActivityLogger();

    useEffect(() => {
        logVisit('attendance', 'Holiday Calendar', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const body = useMemo(() => ({
        page:1, limit:500, sortField:'date', sortDirection:'asc', status:'active',
        startDate: new Date(yr,0,1).toISOString(),
        endDate:   new Date(yr,11,31).toISOString(),
    }), [yr]);

    const { data, isLoading } = useSWR([endpoints.holidayCalendar.list, body], postFetcher, { revalidateOnFocus:false });
    const holidays = useMemo(() => data?.data || [], [data]);

    // Build a map: monthIndex → { "YYYY-MM-DD": holidayObj }
    const byMonth = useMemo(() => {
        const map = {};
        for (let m = 0; m < 12; m += 1) map[m] = {};
        holidays.forEach(h => {
            const d = new Date(h.date);
            const m = d.getMonth();
            const key = `${yr}-${String(m+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            map[m][key] = h;
        });
        return map;
    }, [holidays, yr]);

    const today = new Date(); today.setHours(0,0,0,0);
    const nextHoliday = holidays.find(h => { const d=new Date(h.date); d.setHours(0,0,0,0); return d>=today; });

    // Which months to show
    const allMonths = Array.from({ length:12 }, (_,i)=>i);
    const visibleMonths = allMonths.filter(m => Object.keys(byMonth[m]).length > 0);

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            bgcolor: 'background.default',
            // Optional: Subtle desk mat texture
            backgroundImage: `radial-gradient(${alpha(theme.palette.grey[500], 0.1)} 1px, transparent 1px)`,
            backgroundSize: '20px 20px',
            pt: 5, pb: 10 
        }}>
            <Container maxWidth="lg">
                <TicketWalletHeader holidays={holidays} />

                {isLoading ? <Skelly /> : (
                    <>
                        <GoldenTicket holiday={nextHoliday} />

                        <Box sx={{ mt: 2 }}>
                            {visibleMonths.map(m => (
                                <MonthTicketBatch
                                    key={m}
                                    monthIndex={m}
                                    holidayMap={byMonth[m]}
                                />
                            ))}
                            {visibleMonths.length === 0 && (
                                <Box sx={{ 
                                    textAlign: 'center', py: 10, 
                                    borderRadius: '24px',
                                    border: `2px dashed ${theme.palette.divider}`,
                                    mt: 4
                                }}>
                                    <Iconify icon="solar:ticket-broken-bold-duotone" width={80} sx={{ color: 'text.disabled', mb: 3 }} />
                                    <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 800 }}>No tickets issued for {yr}</Typography>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </Container>
        </Box>
    );
}

